import React, { useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ArrowRight, AlertCircle } from 'lucide-react';
import AuthShell from '../components/AuthShell';
import { authApi, formatApiErrorDetail } from '../lib/authApi';
import { useAuth } from '../context/AuthContext';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [code, setCode] = useState(['', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { refresh } = useAuth();
  const from = location.state?.from || '/home';

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    const joined = code.join('');
    if (joined.length !== 4) {
      setError('Please enter all 4 digits of your access code.');
      return;
    }
    setLoading(true);
    try {
      await authApi.codeLogin({ email, code: joined, remember_me: true });
      await refresh();
      navigate(from, { replace: true });
    } catch (err) {
      if (err.response?.status === 402) {
        navigate(`/pricing?email=${encodeURIComponent(email)}`, { replace: true });
        return;
      }
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell title="Welcome back" subtitle="Enter your email and 4-digit access code">
      <form onSubmit={submit} className="space-y-5" data-testid="login-code-form">
        <FieldEmail value={email} onChange={setEmail} />
        <FieldCode value={code} onChange={setCode} />
        {error && (
          <div role="alert" className="flex items-start gap-2 text-[12.5px] text-[#7A2E0F] bg-[#FBE3D6] border border-[#E8B79A] rounded-md px-3 py-2.5" data-testid="login-error">
            <AlertCircle size={14} className="mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
        )}
        <button
          type="submit"
          disabled={loading}
          className="w-full h-11 inline-flex items-center justify-center gap-1.5 rounded-md bg-[#1A2622] text-[#FBF6EC] text-[14px] font-medium hover:bg-[#2D5F4F] disabled:opacity-60 transition-colors"
          data-testid="login-code-submit"
        >
          {loading ? 'Signing in…' : (<>Sign in <ArrowRight size={14} /></>)}
        </button>
      </form>

      <Link
        to="/pricing"
        className="mt-6 w-full h-11 inline-flex items-center justify-center gap-1.5 rounded-md border border-[#4A7C5E] text-[#2D5F4F] text-[13.5px] font-medium hover:bg-[#E5F0EA] transition-colors"
        data-testid="login-subscribe-link"
      >
        New here? Subscribe — $49.99/month
      </Link>
      <p className="mt-4 text-[12px] text-[#7A857F] leading-relaxed">
        Questions?{' '}
        <a
          href="mailto:calderoncarluis@gmail.com?subject=Question%20%E2%80%94%20Piece%20of%20Cake%20LTC"
          className="text-[#4A7C5E] hover:text-[#2D5F4F] underline underline-offset-2"
          data-testid="login-request-access"
        >
          Email the site owner
        </a>
      </p>
    </AuthShell>
  );
}

function FieldEmail({ value, onChange }) {
  return (
    <label className="block">
      <span className="text-[11.5px] font-semibold uppercase tracking-[0.14em] text-[#3A4A44]">Email</span>
      <input
        type="email" required autoComplete="email" value={value} onChange={(e) => onChange(e.target.value)}
        placeholder="you@example.com"
        className="mt-1.5 w-full h-11 px-3.5 rounded-md bg-white border border-[#E5DCC4] text-[14px] text-[#1A2622] placeholder:text-[#A8B0AA] focus:border-[#4A7C5E] outline-none transition-colors"
        data-testid="login-email"
      />
    </label>
  );
}

function FieldCode({ value, onChange }) {
  const refs = [useRef(null), useRef(null), useRef(null), useRef(null)];
  const update = (idx, char) => {
    const digit = char.replace(/[^0-9]/g, '').slice(-1);
    if (!digit && char !== '') return;
    const next = [...value];
    next[idx] = digit;
    onChange(next);
    if (digit && idx < 3) refs[idx + 1].current?.focus();
  };
  const onKeyDown = (idx, e) => {
    if (e.key === 'Backspace' && !value[idx] && idx > 0) {
      refs[idx - 1].current?.focus();
    }
  };
  const onPaste = (e) => {
    const txt = (e.clipboardData.getData('text') || '').replace(/[^0-9]/g, '').slice(0, 4);
    if (!txt) return;
    e.preventDefault();
    const next = ['', '', '', ''];
    for (let i = 0; i < txt.length; i++) next[i] = txt[i];
    onChange(next);
    refs[Math.min(txt.length, 3)].current?.focus();
  };
  return (
    <label className="block">
      <span className="text-[11.5px] font-semibold uppercase tracking-[0.14em] text-[#3A4A44]">Access code</span>
      <div className="mt-1.5 flex gap-2 justify-between" onPaste={onPaste}>
        {[0, 1, 2, 3].map((i) => (
          <input
            key={i}
            ref={refs[i]}
            type="text"
            inputMode="numeric"
            maxLength={1}
            autoComplete={i === 0 ? 'one-time-code' : 'off'}
            value={value[i]}
            onChange={(e) => update(i, e.target.value)}
            onKeyDown={(e) => onKeyDown(i, e)}
            className="w-full h-14 text-center text-[22px] font-semibold tracking-widest rounded-md bg-white border border-[#E5DCC4] text-[#1A2622] placeholder:text-[#D4C9AF] focus:border-[#4A7C5E] outline-none transition-colors"
            placeholder="•"
            data-testid={`login-code-digit-${i}`}
          />
        ))}
      </div>
    </label>
  );
}
